/*
 * PD Variant Tiles (GRG-55) — image + name + price-delta tiles for variant
 * options, replacing the Choices.js dropdown on the redesigned PDP.
 *
 * Each <pd-variant-tiles> manages ONE product option (e.g. "Colour"). It keeps a
 * hidden native <select> (the source of truth the theme's <variant-selects>
 * resolves from): clicking a tile sets that select's value and dispatches a
 * bubbling `change`, so VariantSelects.onVariantChange resolves the variant,
 * updates the URL/price/media exactly as the dropdown did.
 *
 * The price-delta on each tile is shown relative to the currently-selected
 * variant — for the value of THIS option, holding the other options fixed
 * (matching Shopify's standard variant-picker delta behaviour). Deltas + the
 * selected/unavailable state are recomputed on every `variant:change`.
 */
if (!customElements.get('pd-variant-tiles')) {
  customElements.define(
    'pd-variant-tiles',
    class PdVariantTiles extends HTMLElement {
      connectedCallback() {
        this.optionIndex = parseInt(this.dataset.optionIndex, 10) || 0;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.variantSelects = this.closest('variant-selects');
        this.nativeSelect = this.querySelector('select');
        this.tiles = Array.from(this.querySelectorAll('[data-variant-tile]'));
        this.variants = this.readVariants();

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));

        // Re-sync deltas + active/unavailable state whenever the variant changes
        // (whether from this option, the slider option, or anywhere else).
        this._onVariantChange = this.refresh.bind(this);
        document.addEventListener('variant:change', this._onVariantChange);

        this.refresh();
      }

      disconnectedCallback() {
        document.removeEventListener('variant:change', this._onVariantChange);
      }

      /* ---------- data ---------- */

      readVariants() {
        const tag =
          this.variantSelects && this.variantSelects.querySelector('script[type="application/json"]');
        if (!tag) return [];
        try {
          return JSON.parse(tag.textContent) || [];
        } catch (e) {
          return [];
        }
      }

      // Current option tuple, read positionally from every <select> — identical to
      // VariantSelects.updateOptions, so indices line up with variant.options.
      currentOptions() {
        if (!this.variantSelects) return [];
        return Array.from(this.variantSelects.querySelectorAll('select'), (s) => s.value);
      }

      findVariant(options) {
        return this.variants.find(
          (v) =>
            v.options &&
            v.options.length === options.length &&
            v.options.every((o, i) => o === options[i])
        );
      }

      /* ---------- money (shared helpers from assets/pd-money.js) ---------- */

      formatMoney(cents) {
        return window.pdFormatMoney(cents, this.moneyFormat);
      }

      signedDelta(deltaCents) {
        return window.pdSignedDelta(deltaCents, this.moneyFormat);
      }

      /* ---------- interaction ---------- */

      onClick(evt) {
        const tile = evt.target.closest('[data-variant-tile]');
        if (tile && this.contains(tile)) this.choose(tile);
      }

      onKeydown(evt) {
        if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(evt.key)) return;
        const current = evt.target.closest('[data-variant-tile]');
        if (!current) return;
        const idx = this.tiles.indexOf(current);
        const forward = evt.key === 'ArrowRight' || evt.key === 'ArrowDown';
        const next = this.tiles[(idx + (forward ? 1 : this.tiles.length - 1)) % this.tiles.length];
        if (next) {
          evt.preventDefault();
          this.choose(next);
          next.focus();
        }
      }

      choose(tile) {
        if (!this.nativeSelect) return;
        const value = tile.dataset.variantValue;
        if (this.nativeSelect.value === value) return;
        this.nativeSelect.value = value;
        // Drives the theme's <variant-selects> resolution (URL, price, media).
        this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        // variant:change will refresh us, but refresh now for snappy feedback.
        this.refresh();
      }

      /* ---------- render ---------- */

      refresh() {
        const options = this.currentOptions();
        const selectedValue = options[this.optionIndex];
        const current = this.findVariant(options);

        this.tiles.forEach((tile) => {
          const value = tile.dataset.variantValue;

          // Variant reachable by swapping only THIS option to `value`.
          const candidate = options.slice();
          candidate[this.optionIndex] = value;
          const variant = this.findVariant(candidate);

          const isSelected = value === selectedValue;
          tile.classList.toggle('is-selected', isSelected);
          tile.setAttribute('aria-checked', isSelected ? 'true' : 'false');
          tile.tabIndex = isSelected ? 0 : -1;

          // Dim combinations that don't map to a real variant; still selectable
          // so the theme can surface its own unavailable messaging.
          const unavailable = !variant || variant.available === false;
          tile.classList.toggle('is-unavailable', unavailable);

          const deltaEl = tile.querySelector('[data-variant-delta]');
          if (deltaEl && variant) {
            const base = current ? current.price : variant.price;
            deltaEl.textContent = this.signedDelta(variant.price - base);
          }
        });
      }
    }
  );
}
